import { Box, Grid, GridItem } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import type { SingleValue } from 'chakra-react-select';
import { type FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftInput from '@/components/Atoms/FaceliftInput';
import FaceliftSelect from '@/components/Atoms/FaceliftSelect/FaceliftSelect';
import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';
import FaceliftDatePicker, {
	getTodayInNYTimezone,
} from '@/components/Molecules/FaceliftDatePicker';
import FaceliftSuccessProgressModal from '@/components/Molecules/FaceliftSuccessProgressModal';
import Show from '@/components/Molecules/Show';
import {
	BgProcessModule,
	useUpdateBgProcess,
} from '@/hooks/service-hooks/bgProcess';
import { useGetPatientsAi } from '@/hooks/service-hooks/patients';
import { usePaginatedSelect } from '@/hooks/usePaginatedSelect';
import { useAuthStore, useClinicStore } from '@/store';
import type { IConfirmedTissue } from '@/types/confirmedGrafts';
import type { Patients } from '@/types/patient';
import { formatDate, formatDateInUTC } from '@/utils/dateUtils';

interface IUtilityEditProps {
	type?: 'assign' | 'confirm';
	mode?: 'create' | 'edit' | 'details';
	initialData: IConfirmedTissue | any;
	onSuccess: () => void;
	isLoading?: boolean;
	onIsDirtyChange?: (isDirty: boolean) => void;
}

const schema = yup.object().shape({
	Clinic: yup.object().required(),
	Physician: yup.object().required(),
	Patient: yup.object<SelectItem>().required('Patient is required'),
	Product: yup.object().required(),
	Size: yup.object().required(),
	TissueID: yup.object().required(),
	EMRID: yup.string().required(),
	DateofService: yup.string().required(),
});

export type UtilityEditFormType = yup.InferType<typeof schema>;

export const EditForm: FC<IUtilityEditProps> = ({
	type = 'assign',
	mode,
	initialData,
	onSuccess,
	isLoading = false,
	onIsDirtyChange,
}) => {
	const {
		handleSubmit,
		setValue,
		reset,
		watch,
		getValues,
		formState: { errors, isDirty },
	} = useForm<UtilityEditFormType>({
		resolver: yupResolver(schema),
		defaultValues: {
			Clinic: undefined,
			Physician: undefined,
			Patient: undefined,
			Product: undefined,
			Size: undefined,
			TissueID: undefined,
			EMRID: '',
			DateofService: '',
		},
	});

	const user = useAuthStore((state) => state.user);
	const userId = user?.userInformation?._id || null;
	const allClinics = useClinicStore((state) => state.currentClinics);

	// ➤ Set initial data on mount
	useEffect(() => {
		if (initialData) {
			const formattedData = {
				Clinic: {
					label: initialData.clinic || initialData.Clinic,
					value: initialData.clinic || initialData.Clinic,
				},
				Physician: {
					label: initialData.physician || initialData.Physician,
					value: initialData.physician || initialData.Physician,
				},
				Patient: {
					label: initialData.patient || initialData.Patient,
					value: initialData.patient || initialData.Patient,
				},
				Product: {
					label: initialData.product || initialData.Product,
					value: initialData.product || initialData.Product,
				},
				Size: {
					label: initialData.size || initialData.Size,
					value: initialData.size || initialData.Size,
				},
				TissueID: {
					label: initialData.tissueid || initialData.TissueID,
					value: initialData.tissueid || initialData.TissueID,
				},
				EMRID: initialData.emrid || initialData.EMRID,
				DateofService:
					type === 'assign'
						? formatDate(initialData.proceduredate || initialData.DateofService)
						: formatDate(initialData.DateofService),
			};
			reset(formattedData, { keepDefaultValues: false, keepDirty: false });
			if (onIsDirtyChange) {
				onIsDirtyChange(true);
			}
		} else {
			if (onIsDirtyChange) {
				onIsDirtyChange(true);
			}
		}
	}, [initialData, reset, type]);

	useEffect(() => {
		if (onIsDirtyChange && initialData) {
			onIsDirtyChange(!isDirty);
		}
	}, [isDirty, initialData]);

	const {
		mutate: updateUnAssignedGraft,
		isLoading: updateUnAssignedLoading,
		isError: updateUnAssignedError,
		isSuccess,
	} = useUpdateBgProcess(() => {
		setTimeout(() => {
			onSuccess?.();
		}, 2000);
	});

	const {
		mutate: updateConfirmedGraft,
		isLoading: updateConfirmedLoading,
		isError: updateConfirmedError,
		isSuccess: isConfirmedSuccess,
	} = useUpdateBgProcess(() => {
		setTimeout(() => {
			onSuccess?.();
		}, 2000);
	});

	const handleFormSubmit = handleSubmit((values) => {
		const assignPayload = {
			GraftLogsID: 0,
			Patient: (getValues('Patient') as SelectItem)?.label,
			GraftLogsType: 'Assign',
			Clinic: (getValues('Clinic') as SelectItem)?.label,
			Product: (getValues('Product') as SelectItem)?.label,
			Size: (getValues('Size') as SelectItem)?.label,
			Physician: (getValues('Physician') as SelectItem)?.label,
			TissueID: (getValues('TissueID') as SelectItem)?.label,
			DateofService: formatDateInUTC(getValues('DateofService')),
			EMRID: getValues('EMRID') ?? 'UNK',
			InventoryRowid1: initialData?.inventoryrowid1 ?? '',
			UniqueID: initialData?.inventoryrowid1 ?? '',
			Type: 'Graftlogs',
			ta_originationsource: '953870002',
		};

		const confirmedPayload = {
			GraftLogsID: initialData?.GraftLogsID,
			Patient: (getValues('Patient') as SelectItem)?.label,
			GraftLogsType: 'Edit',
			Clinic: (getValues('Clinic') as SelectItem)?.label,
			Product: (getValues('Product') as SelectItem)?.label,
			Size: (getValues('Size') as SelectItem)?.label,
			Physician: (getValues('Physician') as SelectItem)?.label,
			TissueID: (getValues('TissueID') as SelectItem)?.label,
			DateofService: formatDateInUTC(getValues('DateofService')),
			EMRID: getValues('EMRID') ?? 'UNK',
			ta_originationsource: '953870002',
		};
		const flag =
			initialData?.patient !== (getValues('Patient') as SelectItem)?.label
				? 1
				: 0;

		type === 'assign'
			? updateUnAssignedGraft({
					moduleName: BgProcessModule.UTILITIES_ASSIGN_GRAFTS,
					userId: userId || '',
					clinicId:
						allClinics.find(
							(item) =>
								item.name.trim().toLowerCase() ===
								(getValues('Clinic') as SelectItem)?.label.trim().toLowerCase(),
						)?.accountid || '',
					payload: {
						payload: assignPayload,
						additionalPayload: {
							clinicName: (getValues('Clinic') as SelectItem)?.label as string,
							patientName: (getValues('Patient') as SelectItem)
								?.label as string,
							physicianName: (getValues('Physician') as SelectItem)
								?.label as string,
							productName: (getValues('Product') as SelectItem)
								?.label as string,
						},
					},
					flag,
				})
			: updateConfirmedGraft({
					moduleName: BgProcessModule.UTILITIES_CONFIRM_GRAFTS,
					userId: userId || '',
					clinicId:
						allClinics.find(
							(item) =>
								item.name.trim().toLowerCase() ===
								(getValues('Clinic') as SelectItem)?.label.trim().toLowerCase(),
						)?.accountid || '',
					payload: {
						payload: confirmedPayload,
						additionalPayload: {
							clinicName: (getValues('Clinic') as SelectItem)?.label as string,
							patientName: (getValues('Patient') as SelectItem)
								?.label as string,
							physicianName: (getValues('Physician') as SelectItem)
								?.label as string,
							productName: (getValues('Product') as SelectItem)
								?.label as string,
						},
					},
				});
	});

	// Patients with pagination
	const patients = usePaginatedSelect({
		queryHook: useGetPatientsAi,
		queryParams: {
			clinicId: allClinics.find(
				(clinic) => clinic.name === (watch('Clinic') as SelectItem)?.value,
			)?.accountid as string,
			enabled: !!watch('Clinic'),
		},
		transform: (patient: Patients) => ({
			value: patient.contactid,
			label: patient.fullname,
		}),
		errorMessage: 'Unable to load patients',
		resetKey: !!watch('Clinic'),
	});

	return (
		<>
			<form onSubmit={handleFormSubmit}>
				<Grid templateColumns={{ base: '1fr', md: '2fr 1fr' }} gap={4} px={1}>
					{/* LEFT SIDE – EXACT SAME UI AS CREATE */}
					<GridItem gap={4}>
						<Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
							{/* Clinic */}
							<GridItem>
								<FaceliftSelect
									topLabel="Clinic"
									placeholder="Select Clinic"
									value={watch('Clinic')}
									items={[watch('Clinic')]}
									isDisabled
									required
								/>
							</GridItem>

							{/* Provider */}
							<GridItem>
								<FaceliftSelect
									topLabel="Provider"
									placeholder="Select Provider"
									value={watch('Physician')}
									items={[watch('Physician')]}
									isDisabled
									required
								/>
							</GridItem>

							{/* Product */}
							<GridItem>
								<FaceliftSelect
									topLabel="Product"
									placeholder="Select Product"
									value={watch('Product')}
									items={[watch('Product')]}
									isDisabled
									required
								/>
							</GridItem>

							{/* Size */}
							<GridItem>
								<FaceliftSelect
									topLabel="Size"
									placeholder="Select Size"
									value={watch('Size')}
									items={[watch('Size')]}
									isDisabled
									required
								/>
							</GridItem>

							{/* Tissue ID */}
							<GridItem mt={4}>
								<FaceliftSelect
									topLabel="Tissue ID"
									placeholder="Select Tissue ID"
									value={watch('TissueID')}
									items={[watch('TissueID')]}
									isDisabled
									required
								/>
							</GridItem>

							{/* EMR ID */}
							<GridItem mt={4}>
								<FaceliftInput
									topLabel="EMR ID"
									placeholder="000-000-000"
									value={watch('EMRID')}
									disabled
									error={errors.EMRID?.message}
								/>
							</GridItem>
						</Grid>
					</GridItem>

					{/* RIGHT SIDE – EXACT SAME UI */}
					<GridItem gap={4}>
						<Box display="flex" flexDirection="column" gap={4}>
							{/* Patient */}
							<FaceliftSelect<SelectItem, false>
								topLabel="Patient"
								placeholder="Select Patient"
								value={watch('Patient') as SelectItem}
								onChange={(newValue: SingleValue<SelectItem>) => {
									if (newValue) {
										setValue('Patient', newValue, { shouldDirty: true });
									}
								}}
								items={patients.items}
								loading={patients.loading}
								error={errors.Patient?.message}
								onLoadMore={patients.onLoadMore}
								isDisabled={!watch('Clinic') || mode === 'details'}
								isSearchable
								required
								ai
								aiSuggestionText={
								'Use a keyword like “Smith” to see more relevant results'
								}
							/>

							{/* Date of Service (ONLY EDITABLE FIELD) */}
							<FaceliftDatePicker
								topLabel="DOS"
								value={watch('DateofService')}
								onChange={(date: string) => setValue('DateofService', date, { shouldDirty: true })}
								placeholder="MM/DD/YYYY"
								error={errors.DateofService?.message}
								required
								minDate={getTodayInNYTimezone()} // Prevent past dates using NY timezone
								defaultToToday
								disabled={mode === 'details'}
							/>
						</Box>
					</GridItem>
				</Grid>

				{/* BUTTONS – EXACT SAME UI */}
				<Show when={mode !== 'details'}>
					<Box display="flex" gap={4} justifyContent="flex-end" pl={14} mt={10}>
						<FaceliftButton
							loading={
								updateUnAssignedLoading || updateConfirmedLoading || isLoading
							}
							disabled={
								updateUnAssignedLoading || updateConfirmedLoading || isLoading
							}
							type="submit"
							cursor="pointer"
							width="44"
						>
							Save
						</FaceliftButton>
					</Box>
				</Show>
			</form>
			<FaceliftSuccessProgressModal
				open={
					updateUnAssignedLoading ||
					updateConfirmedLoading ||
					isSuccess ||
					isConfirmedSuccess ||
					updateUnAssignedError ||
					updateConfirmedError
				}
				isLoading={updateUnAssignedLoading || updateConfirmedLoading}
				isSucceed={isSuccess || isConfirmedSuccess}
				hasError={updateUnAssignedError || updateConfirmedError}
				onOpenChange={() => {}}
				title={
					type === 'assign'
						? 'The graft log has been successfully Assigned!'
						: 'The graft log has been successfully updated!'
				}
				errorMessage={
					updateUnAssignedError || updateConfirmedError
						? type === 'assign'
							? 'Error assigning graft log'
							: 'Error updating graft log'
						: ''
				}
			/>
		</>
	);
};

export default EditForm;
